import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PushPermissionCard,
  PushProvider,
  PushToast,
  pushLinkTarget,
  usePushRegistration,
} from '@kilat/ui';
import type { InboxMessage } from '@kilat/shared';

import { useAuth } from '../use-auth.js';
import { useI18n } from '../use-i18n.js';
import { useTrackedSnapshot } from '../use-tracked-snapshot.js';
import { isAgentInboxMessage, subscribeAgentInbox } from '../repositories/inbox-repo.js';
import { agentPushClient } from './client.js';

/** Below the sticky header row: safe area + one touch target + its vertical padding. */
const TOAST_TOP =
  'calc(var(--safe-top, 0px) + var(--touch-target) + var(--space-3) * 2 + var(--space-2))';

/** Follow a push deep link in-app when it is ours, else load it. */
function useFollowPushLink(): (link: string) => void {
  const nav = useNavigate();
  return useCallback(
    (link: string) => {
      const target = pushLinkTarget(link, {
        base: '/',
        origin: window.location.origin,
        routeRoots: null,
      });
      if (target.routerPath) nav(target.routerPath);
      else window.location.assign(target.href);
    },
    [nav],
  );
}

/** One push provider for the agent app, inside the router so taps route in-app. */
export function AgentPushProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const follow = useFollowPushLink();
  return (
    <PushProvider client={agentPushClient} uid={user?.uid ?? null} onNavigate={follow}>
      {children}
    </PushProvider>
  );
}

/**
 * Foreground push → a banner on any screen, just below the sticky header row
 * (one touch target plus its vertical padding). The bottom belongs to the tab
 * bar and the update banner.
 */
export function AgentPushToast() {
  const t = useI18n();
  const { latestMessage, dismissLatest } = usePushRegistration();
  const follow = useFollowPushLink();
  return (
    <PushToast
      message={latestMessage}
      fallbackTitle={t.t('common.push.toastFallbackTitle')}
      openLabel={t.t('common.push.toastOpen')}
      dismissLabel={t.t('common.push.toastDismiss')}
      onOpen={follow}
      onDismiss={dismissLatest}
      top={TOAST_TOP}
    />
  );
}

/** The device push control, for the profile screen. */
export function AgentPushCard() {
  const t = useI18n();
  const push = usePushRegistration();
  return (
    <PushPermissionCard
      state={push.state}
      busy={push.busy}
      registrationFailed={push.registrationFailed}
      onEnable={() => void push.enable()}
      copy={{
        title: t.t('common.push.cardTitle'),
        checking: t.t('common.push.checking'),
        on: { status: t.t('common.push.onStatus'), body: t.t('agent.push.onBody') },
        ask: {
          status: t.t('common.push.askStatus'),
          body: t.t('agent.push.askBody'),
          action: t.t('common.push.enableAction'),
          busyAction: t.t('common.push.enableBusy'),
        },
        blocked: { status: t.t('common.push.blockedStatus'), body: t.t('common.push.blockedBody') },
        iosInstall: { status: t.t('common.push.iosStatus'), body: t.t('common.push.iosBody') },
        unsupported: {
          status: t.t('common.push.unsupportedStatus'),
          body: t.t('common.push.unsupportedBody'),
        },
        registrationFailed: t.t('common.push.registrationFailed'),
        retry: t.t('common.push.retry'),
      }}
    />
  );
}

/**
 * Live agent inbox, shared by the header bell and the /inbox screen. Errors are
 * surfaced to the caller (the screen owns the visible error state; the bell
 * stays quiet).
 */
export function useAgentInbox(onError: (err: Error) => void, retryNonce = 0) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const subscribe = useMemo(() => {
    if (!uid) return null;
    return (cb: (vs: InboxMessage[]) => void) => subscribeAgentInbox(uid, cb, onError);
    // onError is a stable setter from the caller; retryNonce forces a fresh listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, retryNonce]);
  const { data, loading } = useTrackedSnapshot<InboxMessage[]>(
    ['agent-inbox', uid, retryNonce],
    subscribe,
  );
  const messages = useMemo(() => (data ?? []).filter(isAgentInboxMessage), [data]);
  return { uid, messages, loading };
}
